import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { loginSchema, type LoginFormValues } from '@/schemas';
import { AuthCard, FormSelect, FormTextField, PasswordField } from '@/components';
import { BUSINESS_TYPE_LOGIN_OPTIONS } from '@/constants/options';
import { brand } from '@/theme/theme';

interface LocationState {
  from?: { pathname?: string };
  registered?: boolean;
  defaultUsername?: string;
  defaultPassword?: string;
  businessType?: 'WHOLESALER' | 'RETAILER' | 'REPRESENTATIVE' | 'OTHERS';
}

/**
 * Map a thrown error from the login call to a message the user can act on.
 *
 * Priority:
 *   1. Backend's `message` field (BusinessException / ApiError shape).
 *   2. Status-code-specific fallback (401 → bad credentials, 403 → disabled, …).
 *   3. Network / unknown fallback.
 *
 * This is intentionally defensive — different backends and proxies surface
 * errors in slightly different shapes (axios, fetch, gateway HTML pages),
 * so we read whichever fields happen to exist.
 */
function describeLoginError(err: unknown): string {
  // Axios-style: err.response = { status, data: { message, code } }
  // Fetch-style: err.status, err.body
  // Plain Error : err.message
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;

  const status: number | undefined =
    e?.response?.status ?? e?.status ?? e?.statusCode;

  const backendMessage: string | undefined =
    e?.response?.data?.message ??
    e?.response?.data?.error ??
    e?.data?.message ??
    e?.body?.message ??
    (typeof e?.message === 'string' && e.message !== 'Network Error' && !/^Request failed with status code/.test(e.message) ? e.message : undefined);

  // Network / CORS / server unreachable
  if (e?.message === 'Network Error' || e?.code === 'ERR_NETWORK' || status === 0) {
    return 'Cannot reach the server. Check your internet connection and try again.';
  }

  // Backend gave us something useful — use it.
  if (backendMessage && backendMessage.toLowerCase() !== 'unknown error') {
    return backendMessage;
  }

  // Status-code fallbacks for when the backend message is missing or generic.
  switch (status) {
    case 400:
      return 'Some details are missing or invalid. Please check the form and try again.';
    case 401:
      return 'Invalid credentials. Please check your username, password, and business type.';
    case 403:
      return 'Your account is disabled or locked. Please contact your administrator.';
    case 404:
      return 'No account found for the selected business type.';
    case 409:
      return 'Account conflict. Please contact your administrator.';
    case 423:
      return 'Account is locked due to too many failed sign-in attempts. Try again later.';
    case 429:
      return 'Too many sign-in attempts. Please wait a moment and try again.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'The server is having trouble right now. Please try again in a moment.';
    default:
      return 'Sign in failed. Please double-check your credentials and selected business type.';
  }
}

interface LoginPageProps {
  /**
   * When set, the business-type dropdown is hidden and login is locked to
   * this type. Used by the unlinked /super-admin-login route — super-admin
   * sign-in is intentionally not a selectable option on the regular /login
   * page; this route must be typed in directly by whoever needs it.
   */
  lockBusinessType?: 'OTHERS';
}

export function LoginPage({ lockBusinessType }: LoginPageProps = {}) {
  const { login, loading, isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation() as { state?: LocationState; pathname: string };

  // If already authenticated (e.g. token still valid after refresh), redirect immediately.
  useEffect(() => {
    if (isAuthenticated) {
      const defaultPath = user?.tenantType === 'RETAILER' ? '/orders' : '/dashboard';
      navigate(location.state?.from?.pathname ?? defaultPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location.state?.from?.pathname]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [registeredBanner, setRegisteredBanner] = useState<{
    username: string;
    password: string;
  } | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      usernameOrPhone: location.state?.defaultUsername ?? '',
      password: '',
      businessType: lockBusinessType ?? location.state?.businessType ?? 'WHOLESALER',
    },
  });

  // If we got here after a successful registration, surface the credentials once.
  useEffect(() => {
    if (location.state?.registered) {
      setRegisteredBanner({
        username: location.state.defaultUsername ?? 'admin',
        password: location.state.defaultPassword ?? '',
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (values: LoginFormValues) => {
    setServerError(null);
    try {
      await login(values);
      const user = useAuthStore.getState().user;
      const defaultPath = user?.tenantType === 'RETAILER' ? '/orders' : '/dashboard';
      const redirectTo = location.state?.from?.pathname || defaultPath;
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setServerError(describeLoginError(err));
    }
  };

  return (
    <AuthCard
      footer={
        <Stack spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ color: brand.inkSoft }}>
            Don't have an account?{' '}
            <Link
              component="button"
              onClick={() => navigate('/register-tenant')}
              sx={{ fontWeight: 600, color: brand.violet, textDecoration: 'none' }}
            >
              Register your business
            </Link>
          </Typography>
          {!lockBusinessType && (
            <Link
              component="button"
              onClick={() => navigate('/super-admin-login')}
              sx={{ fontSize: 11, color: brand.inkSoft, textDecoration: 'none', opacity: 0.6, '&:hover': { opacity: 1 } }}
            >
              Platform admin login
            </Link>
          )}
        </Stack>
      }
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h2" sx={{ color: brand.navy, mb: 0.5 }}>
          {lockBusinessType ? 'Platform Admin Sign In' : 'Welcome back'}
        </Typography>
        <Typography variant="body2" sx={{ color: brand.inkSoft }}>
          {lockBusinessType ? 'Super-admin access only' : 'Sign in to your Interface workspace'}
        </Typography>
      </Box>

      {registeredBanner && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Workspace created.{' '}
          {registeredBanner.password ? (
            <>
              Sign in with default credentials —{' '}
              <strong>{registeredBanner.username}</strong> /{' '}
              <strong>{registeredBanner.password}</strong>. Change them after
              signing in.
            </>
          ) : (
            <>
              Sign in as <strong>{registeredBanner.username}</strong> with
              the password you chose.
            </>
          )}
        </Alert>
      )}

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError(null)}>
          {serverError}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          {!lockBusinessType && (
            <FormSelect
              name="businessType"
              control={control}
              errors={errors}
              label="Business type"
              options={[...BUSINESS_TYPE_LOGIN_OPTIONS]}
            />
          )}
          <FormTextField
            name="usernameOrPhone"
            register={register}
            errors={errors}
            label="Username or phone number"
            autoComplete="username"
            autoFocus={!location.state?.defaultUsername}
          />
          <PasswordField
            name="password"
            register={register}
            errors={errors}
            label="Password"
            autoComplete="current-password"
            autoFocus={!!location.state?.defaultUsername}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ height: 48, mt: 0.5 }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
